/**
 * Currency Utilities Library
 *
 * Centralized currency handling functions for Brazilian Real (R$) with consistent
 * formatting, parsing, and conversion operations across the application.
 *
 * @module lib/utils/currencyUtils
 */

/**
 * Options for currency formatting
 */
export interface FormatCurrencyOptions {
  /**
   * Include currency symbol (R$)
   * @default true
   */
  includeSymbol?: boolean;

  /**
   * Minimum fraction digits
   * @default 2
   */
  minimumFractionDigits?: number;

  /**
   * Maximum fraction digits
   * @default 2
   */
  maximumFractionDigits?: number;

  /**
   * Locale to use for formatting
   * @default 'pt-BR'
   */
  locale?: string;
}

/**
 * Formats a number as Brazilian currency (R$)
 *
 * @param amount - Amount to format (can be number, null, or undefined)
 * @param options - Formatting options
 * @returns Formatted currency string (e.g., "R$ 1.234,56")
 *
 * @example
 * formatCurrency(1234.56) // "R$ 1.234,56"
 * formatCurrency(1234.56, { includeSymbol: false }) // "1.234,56"
 * formatCurrency(null) // ""
 * formatCurrency(undefined) // ""
 */
export function formatCurrency(
  amount?: number | null,
  options: FormatCurrencyOptions = {}
): string {
  if (amount === undefined || amount === null || Number.isNaN(amount)) {
    return "";
  }

  const {
    includeSymbol = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = "pt-BR",
  } = options;

  const formatted = amount.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return includeSymbol ? `R$ ${formatted}` : formatted;
}

/**
 * Parses a Brazilian currency string to a number
 *
 * Handles both Brazilian format (1.234,56) and US format (1,234.56)
 * Automatically detects format based on comma/dot usage
 *
 * @param input - Currency string to parse
 * @returns Parsed number or undefined if invalid
 *
 * @example
 * parseCurrency("1.234,56") // 1234.56
 * parseCurrency("R$ 1.234,56") // 1234.56
 * parseCurrency("1234.56") // 1234.56
 * parseCurrency("") // undefined
 * parseCurrency("invalid") // undefined
 */
export function parseCurrency(input: string): number | undefined {
  if (!input) {
    return undefined;
  }

  // Remove currency symbols and whitespace, preserving minus sign
  let sanitized = input.replace(/R\$?\s*/g, "").replace(/\s/g, "");

  // Detect format based on comma/dot positions
  const hasComma = sanitized.includes(",");
  const hasDot = sanitized.includes(".");

  if (hasComma && hasDot) {
    // Both comma and dot present - check which comes last
    const lastCommaIndex = sanitized.lastIndexOf(",");
    const lastDotIndex = sanitized.lastIndexOf(".");

    if (lastCommaIndex > lastDotIndex) {
      // Brazilian format: 1.234,56 (comma after dot)
      sanitized = sanitized.replace(/\./g, "");
      sanitized = sanitized.replace(",", ".");
    } else {
      // US format: 1,234.56 (dot after comma)
      sanitized = sanitized.replace(/,/g, "");
    }
  } else if (hasComma) {
    // Only comma - Brazilian format: 123,45
    sanitized = sanitized.replace(",", ".");
  }
  // else: only dot or neither - keep as is

  // Remove any remaining non-numeric characters except dot and minus
  sanitized = sanitized.replace(/[^0-9.-]/g, "");

  if (!sanitized || sanitized === "-") {
    return undefined;
  }

  const parsed = Number(sanitized);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Converts cents to currency (divides by 100)
 *
 * Useful for converting stored integer values to decimal currency amounts
 *
 * @param cents - Amount in cents
 * @returns Amount in currency units
 *
 * @example
 * centsToCurrency(12356) // 123.56
 * centsToCurrency(100) // 1.00
 * centsToCurrency(0) // 0
 */
export function centsToCurrency(cents: number): number {
  if (Number.isNaN(cents)) {
    throw new Error(`Invalid cents value: ${cents}`);
  }

  return cents / 100;
}

/**
 * Converts currency to cents (multiplies by 100)
 *
 * Useful for storing currency as integer values to avoid floating-point issues
 *
 * @param currency - Amount in currency units
 * @returns Amount in cents (rounded to nearest integer)
 *
 * @example
 * currencyToCents(123.56) // 12356
 * currencyToCents(1.005) // 101 (rounded)
 * currencyToCents(0) // 0
 */
export function currencyToCents(currency: number): number {
  if (Number.isNaN(currency)) {
    throw new Error(`Invalid currency value: ${currency}`);
  }

  // Add small epsilon to handle floating-point precision issues
  // e.g., 1.005 * 100 = 100.49999999999999, we want 101
  const cents = currency * 100;
  const epsilon = Number.EPSILON * Math.abs(cents);
  return Math.round(cents + (cents >= 0 ? epsilon : -epsilon));
}

/**
 * Checks if a value is a valid currency amount
 *
 * @param value - Value to validate
 * @returns true if valid currency amount, false otherwise
 *
 * @example
 * isValidCurrency(123.56) // true
 * isValidCurrency(0) // true
 * isValidCurrency(-10) // true (negative values allowed)
 * isValidCurrency(NaN) // false
 * isValidCurrency(null) // false
 * isValidCurrency(undefined) // false
 */
export function isValidCurrency(value: any): boolean {
  if (value == null) return false;
  if (typeof value !== "number") return false;
  return !Number.isNaN(value) && Number.isFinite(value);
}

/**
 * Adds two currency amounts with proper rounding
 *
 * Avoids floating-point precision issues by converting to cents,
 * adding, and converting back
 *
 * @param amount1 - First amount
 * @param amount2 - Second amount
 * @returns Sum of amounts with proper rounding
 *
 * @example
 * addCurrency(123.45, 67.89) // 191.34
 * addCurrency(0.1, 0.2) // 0.3 (no floating-point errors)
 */
export function addCurrency(amount1: number, amount2: number): number {
  if (!isValidCurrency(amount1) || !isValidCurrency(amount2)) {
    throw new Error(
      `Invalid currency values: ${amount1}, ${amount2}`
    );
  }

  const cents1 = currencyToCents(amount1);
  const cents2 = currencyToCents(amount2);
  return centsToCurrency(cents1 + cents2);
}

/**
 * Subtracts one currency amount from another with proper rounding
 *
 * @param amount1 - Amount to subtract from
 * @param amount2 - Amount to subtract
 * @returns Difference with proper rounding
 *
 * @example
 * subtractCurrency(123.45, 67.89) // 55.56
 */
export function subtractCurrency(amount1: number, amount2: number): number {
  if (!isValidCurrency(amount1) || !isValidCurrency(amount2)) {
    throw new Error(
      `Invalid currency values: ${amount1}, ${amount2}`
    );
  }

  const cents1 = currencyToCents(amount1);
  const cents2 = currencyToCents(amount2);
  return centsToCurrency(cents1 - cents2);
}

/**
 * Multiplies a currency amount by a factor with proper rounding
 *
 * @param amount - Currency amount
 * @param factor - Multiplication factor
 * @returns Product with proper rounding
 *
 * @example
 * multiplyCurrency(123.45, 2) // 246.90
 * multiplyCurrency(100, 0.15) // 15.00 (tax calculation)
 */
export function multiplyCurrency(amount: number, factor: number): number {
  if (!isValidCurrency(amount) || !isValidCurrency(factor)) {
    throw new Error(
      `Invalid currency values: ${amount}, ${factor}`
    );
  }

  const cents = currencyToCents(amount);
  return centsToCurrency(Math.round(cents * factor));
}

/**
 * Divides a currency amount by a divisor with proper rounding
 *
 * @param amount - Currency amount
 * @param divisor - Division divisor
 * @returns Quotient with proper rounding
 * @throws Error if divisor is zero
 *
 * @example
 * divideCurrency(100, 3) // 33.33
 * divideCurrency(123.45, 2) // 61.73
 */
export function divideCurrency(amount: number, divisor: number): number {
  if (!isValidCurrency(amount) || !isValidCurrency(divisor)) {
    throw new Error(
      `Invalid currency values: ${amount}, ${divisor}`
    );
  }

  if (divisor === 0) {
    throw new Error("Cannot divide by zero");
  }

  const cents = currencyToCents(amount);
  return centsToCurrency(Math.round(cents / divisor));
}

/**
 * Calculates percentage of a currency amount
 *
 * @param amount - Base currency amount
 * @param percentage - Percentage (e.g., 15 for 15%)
 * @returns Calculated percentage amount
 *
 * @example
 * calculatePercentage(1000, 15) // 150.00 (15% of 1000)
 * calculatePercentage(200, 7.5) // 15.00 (7.5% of 200)
 */
export function calculatePercentage(amount: number, percentage: number): number {
  if (!isValidCurrency(amount) || !isValidCurrency(percentage)) {
    throw new Error(
      `Invalid values: amount=${amount}, percentage=${percentage}`
    );
  }

  return multiplyCurrency(amount, percentage / 100);
}

/**
 * Rounds a currency amount to specified decimal places
 *
 * @param amount - Amount to round
 * @param decimals - Number of decimal places (default: 2)
 * @returns Rounded amount
 *
 * @example
 * roundCurrency(123.456) // 123.46
 * roundCurrency(123.456, 1) // 123.50
 */
export function roundCurrency(amount: number, decimals: number = 2): number {
  if (!isValidCurrency(amount)) {
    throw new Error(`Invalid currency value: ${amount}`);
  }

  const multiplier = Math.pow(10, decimals);
  return Math.round(amount * multiplier) / multiplier;
}
